import { all, call, put, select, spawn, take, takeEvery } from 'redux-saga/effects';

import { selectors as userSelectors, types as userTypes } from '@elab/core/store/user';
import {
    actions as cartActions,
    selectors as cartSelectors,
    types as cartTypes,
} from '@elab/core/store/cart';
import { waitForCartLoading } from '@elab/core/utils/cart/sagaHelpers';
import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import {
    actions as checkoutActions,
    selectors as checkoutSelectors,
} from '@elab/core/store/checkout';
import { selectors as configSelectors } from '@elab/core/store/config';
import logger from '@elab/core/utils/logger';
import isEmpty from '@elab/core/utils/isEmpty';
import constants, {
    adjustMaxAllowedProducts,
    cartTypeConstants,
    featureNames,
    storageNames,
} from '@elab/core/utils/constants';
import { getDeliveryMode } from '@elab/core/utils/delivery/sagaHelpers';
import { checkRts } from '@elab/core/utils/DeliveryModes/sagaHelpers';
import * as storage from '@elab/core/utils/storage';
import analytics from '@elab/core/utils/analytics';
import {
    actions as freeSamplesActions,
    api as freeSamplesApi,
} from '@elab/components/connected/FreeSamples/redux';
import { actions as paymentActions, selectors as paymentSelectors } from '@elab/core/store/payment';
import { actions as storeLocatorActions } from '@elab/core/store/store-locator';
import { checkForbiddenEntriesInStock } from '@elab/core/store/cart/saga';
import { actions as uiActions, types as uiTypes } from '@elab/core/store/ui';

import actions from './actions';
import selectors from './selectors';
import types from './types';
import api from './api';

const { DIGITAL, MIXED } = cartTypeConstants;

export function* initializeDelivery() {
    yield call(waitForCartLoading);
    yield put(actions.loadDeliveryModes());
}

export function* loadDeliveryModes({ actions }) {
    const guid = yield select(cartSelectors.getGuid);
    const code = yield select(cartSelectors.getCode);
    const userId = yield select(userSelectors.getUserId);
    if (!guid && !code) return;
    try {
        const data = yield call(api.loadDeliveryModes, userId, userId ? code : guid);

        yield put(actions.setDeliveryModes(data.deliveryModes || []));
    } catch (e) {
        yield call(logger.error, e);
        yield put(actions.setDeliveryModes([]));
    }

    const cartDeliveryMode = yield call(getDeliveryMode);
    const deliveryMode = cartDeliveryMode.code;

    yield put(checkoutActions.setDeliveryMode(deliveryMode));
    yield call(setDeliveryStatus, deliveryMode);

    const isPayment = yield select(paymentSelectors.getIsPaymentPage);

    if (isPayment) {
        yield call(setDeliveryModeViewOnPayment);
    }
}

export function* setDeliveryModeViewOnPayment() {
    const pickupInStoreDeliveryModeCode = yield select(
        selectors.getCodeOfPickupInStoreDeliveryMode,
    );
    const deliveryModeId = yield select(cartSelectors.getDeliveryModeCode);
    const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);

    if (deliveryModeId === pickupInStoreDeliveryModeCode && deliveryAddress.id) {
        yield put(checkoutActions.setChangeDeliveryAddress({ status: false }));
    }
}

export function* createDeliveryModesLoader({ actions }) {
    const userProfile = yield select(userSelectors.getProfile);
    const cartData = yield select(cartSelectors.getData);

    if (isEmpty(userProfile)) {
        yield take(userTypes.SET_PROFILE);
    }

    if (isEmpty(cartData)) {
        yield take(cartTypes.SET_CART_DATA);
    }

    yield call(waitForCartLoading);

    yield call(loadDeliveryModes, { actions });
    yield call(checkRts);
}

function* watchLoadDeliveryModes() {
    yield call(createDeliveryModesLoader, { actions });
}

export function* refuseFreeSamples(userId, cartId) {
    const selectedSamples = yield select(cartSelectors.getFreeSampleEntries);
    const samplesString = JSON.stringify(selectedSamples.map(({ product }) => product.code));

    yield call(freeSamplesApi.refuseFreeSamples, userId, cartId, true);
    yield call(storage.set, storageNames.freeSamplesRefused, samplesString);
}

export function* restoreFreeSamples(userId, cartId) {
    const refusedStr = yield call(storage.get, storageNames.freeSamplesRefused);
    const restoredFreeSamples = refusedStr ? JSON.parse(refusedStr) : null;
    yield call(storage.remove, storageNames.freeSamplesRefused);

    if (restoredFreeSamples) {
        yield call(freeSamplesApi.refuseFreeSamples, userId, cartId, false);
        const payload = { quantity: 1, showNotification: false };
        yield all(
            restoredFreeSamples.map(code => put(cartActions.addToCart({ code, ...payload }))),
        );
    }
}

export function* setRefuseFreeSamples(deliveryModeId) {
    const freeSamplesFlag = yield select(configSelectors.getFreeSamplesFlag);
    if (!freeSamplesFlag) return;

    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(cartSelectors.getCartId);
    const freeSamplesRefused = yield select(cartSelectors.getFreeSamplesRefused);
    const hasFreeSamples = yield select(
        selectors.hasDeliveryModeFeature,
        deliveryModeId,
        featureNames.freeSamples,
    );
    try {
        if (!hasFreeSamples && !freeSamplesRefused) {
            yield call(refuseFreeSamples, userId, cartId);
        } else if (hasFreeSamples && freeSamplesRefused) {
            yield call(restoreFreeSamples, userId, cartId);
        } else if (hasFreeSamples && !freeSamplesRefused) {
            yield call(storage.remove, storageNames.freeSamplesRefused);
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* setDeliveryMode({
    deliveryModeId,
    storeId,
    redirectLink,
    forceToRedirect,
    shouldSendStoreId,
    fromPdp = false,
}) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(cartSelectors.getCartId);
    const freeSamplesFlag = yield select(configSelectors.getFreeSamplesFlag);
    const previousDeliveryMode = yield select(selectors.getDeliveryMode);
    const addQueryParamCountry = yield select(configSelectors.getAddQueryParamCountry);
    const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);
    try {
        yield put(actions.setDeliveryBlocked(true));
        if (previousDeliveryMode && deliveryModeId !== previousDeliveryMode) {
            yield put(paymentActions.setPaymentMethod(null));
        }
        if (addQueryParamCountry && shouldSendStoreId) {
            yield call(
                api.setDeliveryMode,
                userId,
                cartId,
                deliveryModeId,
                storeId,
                null,
                deliveryCountry,
                shouldSendStoreId,
            );
        } else if (addQueryParamCountry) {
            yield call(
                api.setDeliveryMode,
                userId,
                cartId,
                deliveryModeId,
                storeId,
                null,
                deliveryCountry,
            );
        } else if (shouldSendStoreId) {
            yield call(
                api.setDeliveryMode,
                userId,
                cartId,
                deliveryModeId,
                storeId,
                null,
                false,
                shouldSendStoreId,
            );
        } else {
            yield call(api.setDeliveryMode, userId, cartId, deliveryModeId, storeId);
        }
        yield call(setRefuseFreeSamples, deliveryModeId);
        yield put(cartActions.loadCart());
        yield put(checkoutActions.setDeliveryMode(deliveryModeId));
        if (deliveryModeId === 'mat-pickup-express') {
            yield put(storeLocatorActions.setPickUpExpressStoreID(storeId));
        } else {
            yield put(storeLocatorActions.setPickUpExpressStoreID(null));
        }
        yield take(cartTypes.SET_CART_DATA);

        const shouldRedirect = yield call(checkForMaxAllowedQuantityForDelivery, fromPdp);
        if (freeSamplesFlag) {
            const freeSamplesRefused = yield select(cartSelectors.getFreeSamplesRefused);
            yield put(freeSamplesActions.setRemoveSamplesCheckbox(freeSamplesRefused));
        }
        yield call(setDeliveryStatus, deliveryModeId);

        const adjustedForbiddenEntries = yield call(checkForbiddenEntriesInStock, {});
        if (!adjustedForbiddenEntries) return;

        if (redirectLink && forceToRedirect && shouldRedirect) {
            let isEmpty = false;
            if (shouldRedirect === cartTypes.ADJUST_CART) {
                yield take(cartTypes.SET_CART_DATA);
                isEmpty = yield select(cartSelectors.getIsEmpty);
            }
            if (!isEmpty && shouldRedirect === true) {
                yield put(globalActions.redirect(redirectLink));
            }
        }

        yield call(updateIsOutOfStockEntriesPlural);
        const collectGAInfo = yield select(configSelectors.useCollectGAInformation);
        if (collectGAInfo) {
            const cartData = yield select(cartSelectors.getData);
            const {
                entries: cartEntries = [],
                totalPrice: cartTotalPrice,
                totalItems: cartTotalItems,
                appliedPromotions,
                appliedVouchersAmount,
                deliveryMode: { name: deliveryModeName } = {},
            } = cartData;
            yield spawn(analytics.handleDeliveryOption, {
                deliveryModeName,
                cartEntries,
                cartTotalPrice,
                cartTotalItems,
                appliedPromotions,
                appliedVouchersAmount,
            });
        }
    } catch (e) {
        yield call(logger.error, e);
        yield put(actions.setDeliveryBlocked(false));
    }
}

function* checkForMaxAllowedQuantityForDelivery(fromPdp) {
    const { code } = yield select(cartSelectors.getDeliveryMode);
    const pointOfService = yield select(cartSelectors.getDeliveryPointOfService);
    const cartItems = yield select(cartSelectors.getEntries);
    const maxQuantity = yield select(cartSelectors.maxAllowedProductQuantity);
    let hasOutOfStock = false;
    if (!fromPdp) {
        const outOfStockEntries = yield select(cartSelectors.getOutOfStockEntries);
        hasOutOfStock = !!outOfStockEntries.length;
        if (
            (maxQuantity || hasOutOfStock) &&
            (!code.includes(constants.ccDeliveryName) ||
                (code.includes(constants.ccDeliveryName) && pointOfService.pickupExpressEligible))
        ) {
            const hasProductQuantityThanMaxAllowed =
                cartItems.length && cartItems.some(({ quantity }) => quantity > maxQuantity);
            if (hasProductQuantityThanMaxAllowed || hasOutOfStock) {
                yield put(uiActions.openDialog({ name: adjustMaxAllowedProducts }));
                const action = yield take([cartTypes.ADJUST_CART, uiTypes.CLOSE_DIALOG]);
                return action.type;
            }
        }
    }
    return true;
}

export function* updateIsOutOfStockEntriesPlural() {
    const adjustedCartData = yield select(cartSelectors.getAdjustedCartData);
    const outOfStockEntries = yield select(cartSelectors.getOutOfStockEntries);
    adjustedCartData.isOutOfStockEntriesPlural = outOfStockEntries.length > 1;
    yield put(cartActions.setAdjustedCartData(adjustedCartData));
}

function* watchSetDeliveryMode({ payload }) {
    yield call(setDeliveryMode, payload);
    const gwpConfirmationDialogFlag = yield select(configSelectors.getGwpConfirmationDialog);
    if (gwpConfirmationDialogFlag) yield put(uiActions.closeDialog());
}

function* openGwpDialog({ payload }) {
    yield put(
        uiActions.openDialog({
            name: `gwp-delivery-method-confirmation-${payload.deliveryModeId}`,
        }),
    );
}

export function* setDeliveryStatus(deliveryModeId) {
    if (!deliveryModeId) {
        yield put(actions.setDeliveryBlocked(true));
    } else {
        const blockDeliveryWithoutAddress = yield select(
            configSelectors.blockDeliveryWithoutAddress,
        );

        if (deliveryModeId.toLowerCase().includes(constants.ccDeliveryName)) {
            const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);
            const messagesFromExpPickupValidation = yield select(
                cartSelectors.getExpressPickupValidationMessage,
            );
            const cartType = yield select(cartSelectors.getCartType);
            const cartWithGiftCard = cartType === DIGITAL || cartType === MIXED;
            if (
                !deliveryAddress.id ||
                cartWithGiftCard ||
                !!messagesFromExpPickupValidation.length
            ) {
                yield put(actions.setDeliveryBlocked(true));
                return;
            }
        } else if (
            blockDeliveryWithoutAddress &&
            (deliveryModeId.includes(constants.ppDeliveryType) ||
                deliveryModeId.includes(constants.deliveryModeTypePickup))
        ) {
            const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);

            if (!deliveryAddress.id) {
                yield put(actions.setDeliveryBlocked(true));
                return;
            }
        }

        yield put(actions.setDeliveryBlocked(false));
    }
}

export function* setDeliveryModeOnPaymentStep({ payload }) {
    const userId = yield select(userSelectors.getUserId);
    const { deliveryModeId, paymentPageLink, deliveryAddressLink } = payload;
    const previousDeliveryMode = yield select(checkoutSelectors.getPreviousDeliveryMode);
    const homeDeliveryModeCode = yield select(selectors.getCodeOfHomeDeliveryMode);
    const postPointDeliveryModeCode = yield select(selectors.getCodeOfPostPointDeliveryMode);
    const pickupInStoreDeliveryModeCode = yield select(
        selectors.getCodeOfPickupInStoreDeliveryMode,
    );
    const pickupExpressDeliveryModeCode = yield select(
        selectors.getCodeOfPickupExpressDeliveryMode,
    );

    if (
        previousDeliveryMode !== deliveryModeId &&
        previousDeliveryMode !== pickupExpressDeliveryModeCode
    ) {
        if (deliveryModeId === homeDeliveryModeCode) {
            if (userId) {
                yield call(setDeliveryMode, payload);
                yield put(globalActions.goto(paymentPageLink, true));
            } else {
                yield call(setDeliveryMode, {
                    ...payload,
                    redirectLink: deliveryAddressLink,
                    forceToRedirect: true,
                });
                yield put(cartActions.clearDeliveryAddress());
            }
        } else if (
            deliveryModeId === postPointDeliveryModeCode ||
            deliveryModeId === pickupInStoreDeliveryModeCode
        ) {
            yield put(actions.setDeliveryMode(payload));
            yield put(checkoutActions.setChangeDeliveryAddress({ status: true }));
            yield put(cartActions.clearDeliveryAddress());
        } else {
            yield call(setDeliveryMode, payload);
        }
    } else {
        yield call(setDeliveryMode, payload);
    }
}

export default () => [
    takeEvery(types.SET_DELIVERY_MODE_ON_PAYMENT_STEP, setDeliveryModeOnPaymentStep),
    takeEvery(types.LOAD_DELIVERY_MODES, watchLoadDeliveryModes),
    takeEvery(types.SET_DELIVERY_MODE, watchSetDeliveryMode),
    takeEvery(types.INITIALIZE, initializeDelivery),
    takeEvery(types.OPEN_DIALOG_GWP_DELIVERY_METHOD_CONFIRMATION, openGwpDialog),
];
